/**
 * Match Centre logic — every rule the centre surfaces render from, kept pure
 * so the vitest suite exercises it without a DOM (TOUCHLINE A2/A2b law,
 * UPSTAIRS V3/V4 action mapping, FIDELITY-PASS §4 timers).
 *
 * LAW ANCHORS:
 * - Accordion (TOUCHLINE A2): the action strip NEVER collapses; zones 2+3
 *   share the rest. State-aware defaults — pre-kickoff/coordination → chat
 *   expanded (talking is the job) · result/verify time → stats+actions
 *   expanded (verifying is the job). Manual override respected until the
 *   NEXT match-state change.
 * - Beacon (TOUCHLINE A2b): exists ONLY while (active match) AND (unread
 *   match-chat while not viewing the chat panel). Red pulse when unread
 *   coincides with a critical state. Drag-park per session; a parked beacon
 *   reappears only on a critical-state message.
 * - Verify exits (UPSTAIRS V4): three exits ranked by cheapness, honesty the
 *   biggest target, each mapping onto VerifyResultRequest.action.
 * - Chat freeze (TOUCHLINE A2): post-verification chat stays open 15 min for
 *   GGs, then freezes read-only into match history.
 */
import {
  MATCH_TERMINAL_STATES,
  VerifyResultRequest,
  type Match,
  type MatchId,
  type MatchScore,
  type MatchState,
} from "@matchday/contracts";
import { TIMER_THRESHOLDS } from "@matchday/juice";

// ── Quick-chat presets (TOUCHLINE A2 — VERBATIM, order is law) ───────────────
// Presets do 80% of coordination with zero moderation surface. The ids are the
// typed-event vocabulary (`quickchat_used{preset_id}`, TOUCHLINE A5).
export const QUICK_CHAT_PRESETS = [
  "Ready when you are",
  "2 min 🙏",
  "Good luck",
  "GG",
  "Good game — see you out there",
] as const;
export type QuickChatPreset = (typeof QUICK_CHAT_PRESETS)[number];

export const QUICK_CHAT_PRESET_IDS: Record<QuickChatPreset, string> = {
  "Ready when you are": "qc_ready",
  "2 min 🙏": "qc_two_min",
  "Good luck": "qc_good_luck",
  GG: "qc_gg",
  "Good game — see you out there": "qc_good_game",
};

// ── Copy deck (verbatim lines the components must not restate) ───────────────
/**
 * SCREEN 05 §MATCH CHAT footer, verbatim.
 *
 * Shipped as "Match chat is on the record." — which drops "· visible to VAR",
 * the half that does the work. "On the record" is abstract; "visible to VAR"
 * names the actual human consequence, and this line's whole job is to be read
 * by someone deciding whether to type something they should not.
 */
export const ON_THE_RECORD_LINE = "Match chat is on the record · visible to VAR";

/**
 * CONFIG SEAM — the player's ELO rating.
 *
 * BK, 27 Jul: "ELO needs to be included currently just hard code it as zero and
 * we will need to build the calculation and back end functions to drive this."
 *
 * So this is DELIBERATELY 0 for every player, and it is a placeholder with a
 * decision behind it — not a forgotten stub. SCREEN 02 shows "720 ELO"; the
 * customer-flow prototype lists "Match history & ELO" as a retained asset. The
 * rating system itself does not exist: nothing computes, stores or updates it in
 * any service.
 *
 * WHAT REPLACING THIS NEEDS (the backend work BK flagged):
 *   1. a rating model (K-factor, starting value, provisional period)
 *   2. an `elo` column on players + a history table for auditability
 *   3. a recompute on match verification (match-engine, after VERIFIED)
 *   4. `elo` on PlayerIdentity so every identity surface reads it
 * Then this constant is deleted and the surfaces read the field.
 *
 * Until then every card reads "0 ELO", which is at least uniformly true —
 * nobody has a rating yet — rather than a fabricated number that looks earned.
 */
export const PLAYER_ELO_PLACEHOLDER = 0;

/** The identity-surface rating line: `DIVISION 3 · 0 ELO`. */
export function divisionEloLine(division: number, elo = PLAYER_ELO_PLACEHOLDER): string {
  return `DIVISION ${division} · ${elo} ELO`;
}


// ── SCREEN 05 §Report, verbatim ─────────────────────────────────────────────
/** Replaces a reported bubble IN PLACE — the conversation keeps its shape. */
export const CHAT_HIDDEN_LINE = "Message hidden · reported to ops";
/** The confirm sheet. */
export const REPORT_SHEET_TITLE = "REPORT THIS MESSAGE?";
/**
 * Both halves are true: the hide is immediate and reporter-scoped, and the row
 * lands in chat_message_reports for ops. Neither is a promise we cannot keep.
 */
export const REPORT_SHEET_BODY =
  "It's hidden for you immediately and goes to the ops queue.";
export const REPORT_SHEET_CANCEL = "CANCEL";
export const REPORT_SHEET_CONFIRM = "REPORT";
/** Confirmation toast — the same words the Night Room uses. */
export const REPORT_DONE_TOAST = "HIDDEN · SENT TO OPS";
/** SCREEN 05: long-press duration before the report sheet opens. */
export const LONG_PRESS_MS = 600;
export const FILTER_BLOCK_COPY = "Keep it on the pitch.";
export const DISPUTE_EXPECTATION_LINE =
  "VAR rules on evidence. Fabricated evidence carries the heaviest penalty on BLG.";

// ── Accordion state machine (TOUCHLINE A2) ───────────────────────────────────
export type AccordionZone = "stats" | "chat";

/** Coordination states → chat expanded; result/verify (and settled) → stats. */
export function defaultZoneForState(state: MatchState): AccordionZone {
  switch (state) {
    case "PAIRED":
    case "READY_CHECK":
    case "LOBBY_ARMED":
    case "CARD_LIVE":
    case "CLAIMED":
    case "IN_GAME":
      return "chat";
    default:
      return "stats"; // RESULT_PENDING · VERIFICATION · DISPUTED · terminals
  }
}

export interface AccordionState {
  zone: AccordionZone;
  /** True after a manual header tap; cleared by the next match-state change. */
  overridden: boolean;
  lastMatchState: MatchState;
}

export function accordionInit(state: MatchState): AccordionState {
  return { zone: defaultZoneForState(state), overridden: false, lastMatchState: state };
}

/** New MatchStateResponse tick: same state → no-op; a real transition resets
 *  to that state's default and clears any manual override. */
export function accordionOnMatchState(acc: AccordionState, next: MatchState): AccordionState {
  if (next === acc.lastMatchState) return acc;
  return { zone: defaultZoneForState(next), overridden: false, lastMatchState: next };
}

/** Header tap — respected until the next state change. */
export function accordionOnManualSwap(acc: AccordionState, zone: AccordionZone): AccordionState {
  if (acc.zone === zone) return acc;
  return { ...acc, zone, overridden: true };
}

// ── Match-state classifiers ──────────────────────────────────────────────────
export function isActiveMatchState(state: MatchState): boolean {
  return !MATCH_TERMINAL_STATES.includes(state);
}

/** Critical per TOUCHLINE A2b: lobby window closing / no-show clock (CLAIMED)
 *  and verify pending (VERIFICATION). Routine states never pulse red. */
export function isCriticalMatchState(state: MatchState): boolean {
  return state === "CLAIMED" || state === "VERIFICATION";
}

/**
 * Has THIS player opened the EA lobby link?
 *
 * `match.state === "IN_GAME"` only tells you SOMEBODY has — the engine moves
 * the shared state on the first arrival and records the two sides separately
 * (host_opened_at / joiner_opened_at, migration 0007). Reading the state as if
 * it were per-player is what took the lobby link away from the second player
 * to arrive, handing them SUBMIT RESULT for a game they had no way into.
 */
export function youArrived(data: {
  you_are: "host" | "joiner";
  match: { state: MatchState; host_opened_at: string | null; joiner_opened_at: string | null };
}): boolean {
  const isHost = data.you_are === "host";
  const tapped = (isHost ? data.match.host_opened_at : data.match.joiner_opened_at) !== null;
  if (tapped) return true;
  // THE HOST BUILT THE LOBBY. They never tap their own join link — they are
  // already standing in it in EA FC — so host_opened_at is null for most hosts
  // forever, and everything keyed off it quietly switched off for them: the
  // RESULT LOCK never fired, so a host could walk away from a finished game
  // without filing a score and go straight on to host another (BK, 13 Aug).
  // Only after kickoff: before that a host with a live card is hosting, not
  // late. Mirrors arrivedForObligation() in the engine's occupancy.ts.
  return (
    isHost &&
    (data.match.state === "IN_GAME" ||
      data.match.state === "RESULT_PENDING" ||
      data.match.state === "VERIFICATION")
  );
}

// ── Result owed ──────────────────────────────────────────────────────────────
/**
 * Per-match sessionStorage key marking "this player tapped through to the EA
 * lobby". Scoped to the match id so two matches in one night cannot inherit
 * each other's arrival — that would be fabricated evidence in the no-show rule.
 */
export function lobbyTapKey(matchId: string): string {
  return `md.lobby.${matchId}`;
}

/**
 * The match is waiting for a score from THIS player.
 *
 * THREE conditions, and the third is the one that was missing.
 *
 *  1. The match has kicked off — IN_GAME, or RESULT_PENDING once the other
 *     side has reported.
 *  2. You have not already filed. After submitting you are waiting on your
 *     opponent, not on yourself.
 *  3. YOU WENT TO THE LOBBY. A player who never opened the EA link has not
 *     played anything and cannot owe a score for it.
 *
 * ── WHY (3) EXISTS ──────────────────────────────────────────────────────────
 * `state` is SHARED. The engine flips it to IN_GAME on the FIRST arrival, so
 * the moment one player taps through, this function was telling the OTHER
 * player — still sitting in the match centre, never having seen a lobby — that
 * they owed a result. Everything downstream believed it: the ENTER RESULT
 * banner, the RESULT tab card, and the result lock, which then pinned them to
 * a screen demanding a score for a game they had not been let into.
 *
 * Arrival has been recorded per side since migration 0007 (host_opened_at /
 * joiner_opened_at). Nothing read it. Fixing the ActionStrip CTA earlier today
 * fixed one symptom of that and left this, the cause, in place.
 */
export function resultOwed(
  state: MatchState,
  yourSubmission: unknown,
  youArrivedAtLobby: boolean,
): boolean {
  if (!youArrivedAtLobby) return false;
  if (yourSubmission !== null && yourSubmission !== undefined) return false;
  return state === "IN_GAME" || state === "RESULT_PENDING";
}

// ── Beacon predicate (TOUCHLINE A2b — the exact conditions) ──────────────────
export interface BeaconInput {
  /** The viewer holds a live (non-terminal) match. */
  activeMatch: boolean;
  unreadCount: number;
  /** Chat panel currently expanded and on screen. */
  viewingChat: boolean;
  /** Unread coincides with a critical match state. */
  critical: boolean;
  /** Drag-to-corner park (per session). */
  parked: boolean;
}

/** ONLY (active match) AND (unread while not viewing chat). Parked stays
 *  hidden except when a critical-state message lands — that un-parks it. */
export function beaconVisible(i: BeaconInput): boolean {
  if (!i.activeMatch) return false;
  if (i.unreadCount <= 0) return false;
  if (i.viewingChat) return false;
  if (i.parked && !i.critical) return false;
  return true;
}

/** Red pulse: visible AND unread coincides with a critical state. */
export function beaconCritical(i: BeaconInput): boolean {
  return beaconVisible(i) && i.critical;
}

// ── Verify / conflict exits (UPSTAIRS V4 → VerifyResultRequest) ──────────────
export type ConflictExit = "misread" | "fix" | "upstairs";

export const EXIT_ACTION: Record<ConflictExit, VerifyResultRequest["action"]> = {
  misread: "accept_theirs",
  fix: "correct_mine",
  upstairs: "dispute",
};

export function buildVerifyRequest(
  matchId: MatchId,
  kind: "verify" | ConflictExit,
  opts: { correctedScore?: MatchScore; note?: string } = {},
): VerifyResultRequest {
  const action = kind === "verify" ? "verify" : EXIT_ACTION[kind];
  return VerifyResultRequest.parse({
    match_id: matchId,
    action,
    ...(action === "correct_mine" && opts.correctedScore !== undefined
      ? { corrected_score: opts.correctedScore }
      : {}),
    ...(action === "dispute" && opts.note !== undefined && opts.note.length > 0
      ? { note: opts.note }
      : {}),
  });
}

// ── Chat freeze window (TOUCHLINE A2 — 15 min for GGs, then read-only) ───────
export const CHAT_FREEZE_WINDOW_MS = 15 * 60 * 1000;
export const CHAT_RATE_LIMIT_MS = 5_000; // match chat 1/5s (TOUCHLINE A4)
export const CHAT_MAX_LENGTH = 280;

/**
 * Writable while the match is live (incl. DISPUTED — coordination continues
 * under a VAR check). After a terminal state: open for CHAT_FREEZE_WINDOW_MS
 * from resolved_at, then frozen. A terminal match without resolved_at is
 * already history — frozen.
 */
export function chatWritable(
  state: MatchState,
  resolvedAt: string | null,
  nowMs: number,
): boolean {
  if (isActiveMatchState(state)) return true;
  if (resolvedAt === null) return false;
  return nowMs - Date.parse(resolvedAt) < CHAT_FREEZE_WINDOW_MS;
}

/** ms until the freeze lands, for the countdown line; null when not counting. */
export function freezeCountdownMs(
  state: MatchState,
  resolvedAt: string | null,
  nowMs: number,
): number | null {
  if (isActiveMatchState(state) || resolvedAt === null) return null;
  const left = CHAT_FREEZE_WINDOW_MS - (nowMs - Date.parse(resolvedAt));
  return left > 0 ? left : null;
}

// ── Free-text filter stub (TOUCHLINE A4 — placeholder wordlist) ──────────────
// The real multi-language list (incl. SA vernacular) is built with the
// community team; this stub proves the block path + gentle copy.
export const BLOCKED_WORDS = [
  "fuck",
  "shit",
  "poes",
  "kak",
  "bliksem",
  "msunu",
  "voetsek",
] as const;

export function isTextBlocked(text: string): boolean {
  const low = text.toLowerCase();
  return BLOCKED_WORDS.some((w) => low.includes(w));
}

// ── Unread accounting (opponent/system arrivals beyond what was seen) ────────
export function computeUnread<T extends { player: { id: string } }>(
  messages: readonly T[],
  seenCount: number,
  selfId: string | null,
): number {
  if (seenCount >= messages.length) return 0;
  return messages.slice(seenCount).filter((m) => m.player.id !== selfId).length;
}

// ── System lines from the match record (chat reads as the match's story) ─────
export interface SystemLine {
  at: string; // IsoTimestamp
  text: string;
}

export function systemLines(match: Match): SystemLine[] {
  const lines: SystemLine[] = [];
  if (match.claimed_at !== null) lines.push({ at: match.claimed_at, text: "Match claimed" });
  if (match.link_opened_at !== null)
    lines.push({ at: match.link_opened_at, text: "Lobby link opened" });
  if (match.first_submit_at !== null)
    lines.push({ at: match.first_submit_at, text: "Result submitted" });
  if (match.resolved_at !== null) {
    const text =
      match.state === "COMPLETE"
        ? "Result verified"
        : match.state === "WALKOVER"
          ? "Walkover awarded"
          : match.state === "VOIDED"
            ? "Match voided"
            : "Match closed";
    lines.push({ at: match.resolved_at, text });
  }
  return lines;
}

// ── Deadlines (display-layer stubs — server truth is a filed contract ask) ───
// SEAM: MatchStateResponse carries no lobby/auto-confirm deadline; until the
// contract-change request lands (lobby_expires_at + auto_confirm_at fields),
// the countdowns anchor on the transition timestamps with these windows.
export const LOBBY_WINDOW_MS = 10 * 60 * 1000;
export const AUTO_CONFIRM_WINDOW_MS = 12 * 60 * 1000;

export function lobbyDeadlineMs(match: Match): number | null {
  if (match.state !== "CLAIMED" || match.claimed_at === null) return null;
  return Date.parse(match.claimed_at) + LOBBY_WINDOW_MS;
}

export function autoConfirmDeadlineMs(match: Match): number | null {
  if (match.state !== "VERIFICATION" || match.first_submit_at === null) return null;
  return Date.parse(match.first_submit_at) + AUTO_CONFIRM_WINDOW_MS;
}

// ── Timer tone (FIDELITY-PASS §4: volt >2:00 · amber ≤2:00 · red ≤0:30) ──────
export type TimerTone = "volt" | "amber" | "red";

export function timerTone(remainingMs: number): TimerTone {
  const s = remainingMs / 1000;
  if (s <= TIMER_THRESHOLDS.redPulseAtS) return "red";
  if (s <= TIMER_THRESHOLDS.amberAtS) return "amber";
  return "volt";
}

/** mm:ss, clamped at 0 — scoreboard-clock format for every countdown/elapsed. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ── Result helpers ───────────────────────────────────────────────────────────
export type Outcome = "W" | "D" | "L";

/** Outcome derived from the score, from the viewer's side. The scoreboard's
 *  W/D/L row reflects this — SubmitResultRequest carries score only, so the
 *  declaration can never contradict the numerals. */
export function outcomeFor(score: MatchScore, youAre: "host" | "joiner"): Outcome {
  const yours = youAre === "host" ? score.host_goals : score.joiner_goals;
  const theirs = youAre === "host" ? score.joiner_goals : score.host_goals;
  if (yours > theirs) return "W";
  if (yours < theirs) return "L";
  return "D";
}

/**
 * Did the VIEWER win this walkover?
 *
 * A walkover is a resolved match with no score, so `outcomeFor` cannot read it —
 * the winner is `awarded_to` (migration 0006). Before that column existed the
 * winner lived only in the walkover_awarded event payload, which is why the match
 * strip could only ever say a walkover HAD happened and not who took it.
 *
 * Returns null when `awarded_to` is null: that is an unreadable row, and the
 * caller must keep its neutral line rather than guess an outcome onto a screen
 * the player reads as fact.
 */
export function walkoverOutcome(
  match: Pick<Match, "host_id" | "joiner_id" | "awarded_to">,
  youAre: "host" | "joiner",
): Outcome | null {
  if (match.awarded_to === null) return null;
  const you = youAre === "host" ? match.host_id : match.joiner_id;
  return match.awarded_to === you ? "W" : "L";
}

export function clampGoals(n: number): number {
  return Math.max(0, Math.min(99, Math.trunc(n)));
}
